import { AfterViewInit, Component, computed, effect, ElementRef, inject, input, OnDestroy, signal, ViewChild } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons';

import { BlockType } from '@empathy/survey/base';

import { IBlockForm } from '../../../../models/blocks/block-form.interface';
import { BlockTypeIconConfig } from '../../../../models/blocks/block-type-config.constants';
import { LIST_BLOCK_BATCH_SIZE } from '../../../../models/blocks/list-block-batch-size.constants';
import { SurveyEditorService } from '../../../../services/survey-editor.service';
import { BlockTypePickerComponent } from '../../block-type-picker/block-type-picker.component';

@Component({
  selector: 'empathy-parent-block-form',
  templateUrl: 'parent-block-form.component.html',
  styleUrls: ['./parent-block-form.component.scss']
})
export class ParentBlockFormComponent implements AfterViewInit, OnDestroy {
  @ViewChild('loadMoreSubBlocksTrigger') loadMoreSubBlocksTrigger: ElementRef;

  activeBlock = input.required<FormGroup<IBlockForm>>();

  readonly isLoopParent$ = computed(() => this.activeBlock().value.type === BlockType.LoopParent);

  allowMoreQuestions$ = signal<boolean>(true);
  visibleSubBlockCount$ = signal<number>(LIST_BLOCK_BATCH_SIZE);

  subBlockControls$ = computed(() => {
    const blocks = this.surveyEditorService.surveyEditor$().blocks;
    const activeBlockId = this.activeBlock().value.id;
    const groupedBlockIds = blocks.all$().find((block) => block.id === activeBlockId)?.groupedBlockIds ?? [];
    const controlsById = new Map(blocks.all.controls.map((control) => [control.value.id, control]));

    return groupedBlockIds
      .map((blockId, groupedIndex) => ({ control: controlsById.get(blockId), isSecondChild: groupedIndex > 0 }))
      .filter((subBlock) => !!subBlock.control);
  });

  visibleSubBlocks$ = computed(() => this.subBlockControls$().slice(0, this.visibleSubBlockCount$()));
  hasMoreSubBlocks$ = computed(() => this.visibleSubBlockCount$() < this.subBlockControls$().length);

  blockType = BlockType;
  blockTypeConfig = BlockTypeIconConfig;

  faTrashCan = faTrashCan;

  private surveyEditorService = inject(SurveyEditorService);
  private dialog = inject(MatDialog);
  private loadMoreObserver: IntersectionObserver = null;

  constructor() {
    effect(
      () => {
        this.activeBlock();
        this.visibleSubBlockCount$.set(LIST_BLOCK_BATCH_SIZE);
      },
      { allowSignalWrites: true }
    );
  }

  private get groupedBlockIds(): FormArray {
    return this.activeBlock().get('groupedBlockIds') as FormArray;
  }

  ngAfterViewInit(): void {
    this.loadMoreObserver = new IntersectionObserver((entries) => entries.some((entry) => entry.isIntersecting) && this.expandVisibleSubBlocks(), {
      rootMargin: '300px'
    });

    this.loadMoreObserver.observe(this.loadMoreSubBlocksTrigger.nativeElement);
  }

  ngOnDestroy(): void {
    this.loadMoreObserver?.disconnect();
  }

  onAddBlock(): void {
    const dialogRef = this.dialog.open(BlockTypePickerComponent, { data: { loopsOnly: this.isLoopParent$() } });

    dialogRef.afterClosed().subscribe((blockType: BlockType) => {
      if (!blockType || (this.isLoopParent$() && blockType !== BlockType.Loop)) {
        return;
      }

      const blockId = this.surveyEditorService
        .surveyEditor$()
        .blocks.push(blockType, true, `${this.activeBlock().value.id.replace('B', 'SB')}.${this.groupedBlockIds.length + 1}`);

      this.groupedBlockIds.push(new FormControl(blockId));

      this.surveyEditorService.surveyEditor$().blocks.refreshLists();
      this.visibleSubBlockCount$.update((count) => Math.max(count, this.subBlockControls$().length));
    });
  }

  onRemoveBlock(blockId: string): void {
    const groupedIndex = this.groupedBlockIds.value.indexOf(blockId);

    this.surveyEditorService.surveyEditor$().blocks.removeChildBlock(groupedIndex, blockId);
  }

  onSubBlockTitleChanged(blockId: string): void {
    this.surveyEditorService.surveyEditor$().blocks.refreshBlockTitle(blockId);
  }

  expandVisibleSubBlocks(): void {
    if (this.hasMoreSubBlocks$()) {
      this.visibleSubBlockCount$.update((count) => count + LIST_BLOCK_BATCH_SIZE);
    }
  }
}
